import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { GroomingBookingCalculator } from '../../../../../../Utilities/components/Calculations/GroomingBookingCalculator';
import { TrainingBookingCalculator } from '../../../../../../Utilities/components/Calculations/TrainingBookingCalculator';
import { HotelDiscountCalculator } from '../../../../../../Utilities/components/Calculations/HotelBookingCalculator';
import { StateContext } from '../../../../../../StateProvider';
import { BackButton } from '../../../../../../Utilities/components/Buttons';
import {
  fetchAllFamilyMember,
  fetchAllUsers,
  createStaffBooking,
  fetchClubMemberships,
  fetchActiveDaycareBundles,
  fetchAllServices,
  // fetchServiceAvailability,
} from '../../../../../../Utilities/api';
import './ReceptionCreateBooking.css';

const ReceptionCreateBooking = function () {
  var navigate = useNavigate();
  var ctx = useContext(StateContext) || {};

  // TEMP: mock values for staff soft booking
  // Staff bookings resolve the actual service record instead of using a mock ID.

  /* -------------------------------
     SEARCH STATE
  -------------------------------- */
  var [searchTerm, setSearchTerm] = useState('');
  var [allRows, setAllRows] = useState([]);
  var [results, setResults] = useState([]);
  var [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;
  var [loadingClients, setLoadingClients] = useState(true);
  var [selectedOwner, setSelectedOwner] = useState(null);
  var [ownerPets, setOwnerPets] = useState([]);
  var [membership, setMembership] = useState(null);
  var [bundles, setBundles] = useState([]);
  var [accountLoading, setAccountLoading] = useState(false);
  var [accountError, setAccountError] = useState(false);
  var [services, setServices] = useState([]);
  var [serviceError, setServiceError] = useState(false);
  var [serviceOptions, setServiceOptions] = useState({});
  var [serviceAddons, setServiceAddons] = useState({});
  var [addonCharges, setAddonCharges] = useState({});

  /* -------------------------------
     BOOKING STATE
  -------------------------------- */
  var [bookingType, setBookingType] = useState('');
  var [selectedPets, setSelectedPets] = useState([]);
  var [date, setDate] = useState('');
  var [dateRange, setDateRange] = useState(null);
  var [timeSlot, setTimeSlot] = useState('');
  var [availableSlots, setAvailableSlots] = useState([]);

  // NOTE: basePrice is now staff-editable (manual set price)
  var [basePrice, setBasePrice] = useState('');

  // NOTE: promo supports % or $ amount (already supported; we keep it)
  var [discountType, setDiscountType] = useState('');
  var [benefitMode, setBenefitMode] = useState('none');
  var [discountChoiceTouched, setDiscountChoiceTouched] = useState(false);
  var [selectedBundleId, setSelectedBundleId] = useState('');
  var [discountValue, setDiscountValue] = useState('');
  // Final price is derived from the selected benefit below.

  var [notes, setNotes] = useState('');
  var [error, setError] = useState(null);
  var [submitting, setSubmitting] = useState(false);
  var [paymentChoice, setPaymentChoice] = useState('unpaid');

  /* -------------------------------
     LOAD USERS + PETS
  -------------------------------- */
  useEffect(function () {
    async function load() {
      setLoadingClients(true);
      try {
        var pets = await fetchAllFamilyMember();
        var users = await fetchAllUsers();

        var userMap = {};
        var petsByUser = {};
        var i;

        for (i = 0; i < users.length; i++) {
          userMap[users[i].uid] = users[i];
          petsByUser[users[i].uid] = [];
        }

        for (i = 0; i < pets.length; i++) {
          var p = pets[i];
          if (p.type !== 'Human' && petsByUser[p.userid]) {
            petsByUser[p.userid].push({
              petId: p.id,
              petName: p.name,
              type: p.type,
              breed: p.breed,
              image: p.image
            });
          }
        }

        var rows = [];

        for (i = 0; i < users.length; i++) {
          var u = users[i];
          rows.push({
            kind: 'owner',
            uid: u.uid,
            name: u.username || '',
            email: u.email || '',
            image: u.image,
            type: 'Human',
            pets: petsByUser[u.uid] || []
          });
        }

        for (i = 0; i < pets.length; i++) {
          var pet = pets[i];
          if (pet.type === 'Human') continue;
          var owner = userMap[pet.userid];
          if (!owner) continue;

          rows.push({
            kind: 'pet',
            petId: pet.id,
            name: pet.name,
            image: pet.image,
            type: pet.type,
            breed: pet.breed,
            ownerUid: owner.uid,
            ownerName: owner.username
          });
        }

        setAllRows(rows);
        setResults(rows);
      } catch (e) {
        console.error(e);
        setError('Unable to load clients. Please refresh the page.');
      } finally {
        setLoadingClients(false);
      }
    }

    load();
  }, []);

  /* -------------------------------
     SEARCH FILTER
  -------------------------------- */
  useEffect(function () {
    var s = (searchTerm || '').trim().toLowerCase();
    var out = [];

    for (var i = 0; i < allRows.length; i++) {
      var r = allRows[i];
      var name = (r.name || '').toLowerCase();
      var email = (r.email || '').toLowerCase();
      var ownerName = (r.ownerName || '').toLowerCase();
      var breed = (r.breed || '').toLowerCase();
      var petNames = (r.pets || []).map(p => `${p.petName} ${p.breed || ''} ${p.type || ''}`).join(' ').toLowerCase();

      if (
        name.indexOf(s) > -1 ||
        email.indexOf(s) > -1 ||
        ownerName.indexOf(s) > -1 ||
        breed.indexOf(s) > -1 ||
        petNames.indexOf(s) > -1
      ) {
        out.push(r);
      }
    }

    setResults(out);
    setCurrentPage(1);
  }, [searchTerm, allRows]);

  const totalPages = Math.ceil(results.length / ITEMS_PER_PAGE);
  const visibleResults = results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  /* -------------------------------
     SELECT OWNER
  -------------------------------- */
  function handleSelect(row) {
    var ownerUid = row.kind === 'owner' ? row.uid : row.ownerUid;
    var ownerName = row.kind === 'owner' ? row.name : row.ownerName;

    var pets = [];
    for (var i = 0; i < allRows.length; i++) {
      var r = allRows[i];
      if (r.kind === 'owner' && r.uid === ownerUid) {
        pets = r.pets || [];
        break;
      }
    }

    setSelectedOwner({ uid: ownerUid, name: ownerName });
    setError(null);
    setOwnerPets(pets);
    setSelectedPets([]);
    setBenefitMode('none');
    setSelectedBundleId('');
    setDiscountChoiceTouched(false);
    setPaymentChoice('unpaid');
  }

  useEffect(function () {
    if (!selectedOwner) return;
    let cancelled = false;
    setAccountLoading(true);
    setAccountError(false);
    setMembership(null);
    setBundles([]);
    Promise.allSettled([fetchClubMemberships(), fetchActiveDaycareBundles(selectedOwner.uid)])
      .then(function (values) {
        if (cancelled) return;
        const membershipsLoaded = values[0].status === 'fulfilled';
        const bundlesLoaded = values[1].status === 'fulfilled';
        if (!membershipsLoaded || !bundlesLoaded) setAccountError(true);
        if (membershipsLoaded) {
          const matching = (values[0].value || []).filter(function (m) {
            return String(m.userId) === String(selectedOwner.uid);
          });
          matching.sort(function (x, y) { return (y.id || 0) - (x.id || 0); });
          setMembership(matching.find(function (m) {
            return m.billingStatus === 'active' && !m.cancelledAt;
          }) || matching[0] || null);
        }
        if (bundlesLoaded) setBundles(values[1].value || []);
      })
      .finally(function () { if (!cancelled) setAccountLoading(false); });
    return function () { cancelled = true; };
  }, [selectedOwner]);

  useEffect(function () {
    fetchAllServices().then(function (items) {
      const rows = Array.isArray(items) ? items : Object.values(items || {}).reduce(function (all, group) {
        return all.concat(Array.isArray(group) ? group : []);
      }, []);
      setServices(rows);
    })
      .catch(function () { setServiceError(true); });
  }, []);

  const selectedService = services.find(function (item) {
    return String(item.serviceName || item.servicename || '').toLowerCase() === bookingType.toLowerCase();
  });

  const serviceCatalog = Object.assign({}, ...services.filter(function (v) { return v.serviceName; }).map(function (v) {
    return { [String(v.serviceName).toLowerCase()]: [v] };
  }));
  const groomingSizePrices = selectedService && selectedService.additionalPrices &&
    selectedService.additionalPrices[serviceOptions.selectedSize || ''] || {};
  const groomingAddonCatalog = groomingSizePrices.additionalServices || {};
  const groomingAddons = Object.keys(groomingAddonCatalog).filter(function (key) { return Number.isFinite(Number(groomingAddonCatalog[key])); });
  const groomingAddonFlags = serviceOptions.groomingAddons || {};
  const pricingInput = {
    selectedPets: selectedPets.map(function (p) { return { petType: p.type }; }),
    groomingOptions: Object.assign({}, serviceOptions, { additionalServices: groomingAddonFlags }),
    trainingOptions: serviceOptions,
    additionalServices: serviceAddons,
    foodPreference: serviceOptions.foodPreference,
    dateRange: date && serviceOptions.checkoutDate ? [date + 'T12:00:00', serviceOptions.checkoutDate + 'T12:00:00'] : null
  };
  let calculatedRate = selectedService ? Number(selectedService.price) || 0 : 0;
  if (bookingType === 'Grooming' && serviceOptions.selectedSize && serviceOptions.selectedPackage) {
    calculatedRate = GroomingBookingCalculator(pricingInput, serviceCatalog).originalPrice;
  } else if (bookingType === 'Training' && serviceOptions.selectedTrainingType && serviceOptions.selectedSessionType) {
    calculatedRate = TrainingBookingCalculator(pricingInput, serviceCatalog).originalPrice;
  } else if (bookingType === 'Hotel' && pricingInput.dateRange && selectedPets.length) {
    calculatedRate = HotelDiscountCalculator(pricingInput, serviceCatalog).discountedPrice;
  } else if (bookingType === 'Shuttle') {
    const miles = Number(serviceOptions.distanceMiles);
    calculatedRate += Number.isFinite(miles) && miles >= 0 ? miles * 3 : 0;
  }
  const externalAddonTotal = Object.keys(serviceAddons).reduce(function (total, key) {
    return total + (serviceAddons[key] && !(key === 'walk' && bookingType === 'Grooming') && !(key === 'walk' && bookingType === 'Hotel')
      ? Math.max(0, Number(addonCharges[key]) || 0) : 0);
  }, 0);
  calculatedRate += externalAddonTotal;
  const pricedAddons = bookingType === 'Grooming'
    ? groomingAddons.filter(function (key) { return groomingAddonFlags[key]; }).map(function (key) {
        return { name: key, amount: Number(groomingAddonCatalog[key]) };
      }).concat(serviceAddons.walk ? [{ name: 'Leash walk', amount: 20 }] : [])
    : bookingType === 'Hotel'
      ? (serviceOptions.foodPreference === 'premium' ? [{ name: 'Premium meals (per pet/night)', amount: 10 }] : [])
        .concat(serviceAddons.walk ? [{ name: 'Leash walk', amount: Number(serviceCatalog.walk && serviceCatalog.walk[0].price) || 20 }] : [])
      : [];
  useEffect(function () {
    if (bookingType === 'Grooming' && (!serviceOptions.selectedSize || !serviceOptions.selectedPackage)) return;
    if (bookingType === 'Training' && (!serviceOptions.selectedTrainingType || !serviceOptions.selectedSessionType)) return;
    if (bookingType === 'Hotel' && !pricingInput.dateRange) return;
    if (!selectedService) return;
    setBasePrice(Number(calculatedRate).toFixed(2));
  }, [bookingType, selectedService, serviceOptions, serviceAddons, addonCharges, selectedPets, date]);

  function setOption(name, value) {
    setServiceOptions(function (old) { return Object.assign({}, old, { [name]: value }); });
  }
  function toggleAddon(name) {
    setServiceAddons(function (old) { return Object.assign({}, old, { [name]: !old[name] }); });
  }

  /* -------------------------------
     BASE PRICING
     (kept as-is, but staff can now override basePrice input below)
  -------------------------------- */
  useEffect(function () {
    var prices = {
      Daycare: 38,
      Grooming: 100,
      Training: 90,
      Adventure: 75,
      Hotel: 68,
      Shuttle: 30
    };

    var p = prices[bookingType] || '';
    setBasePrice(p);
    setDiscountType('');
    setDiscountValue('');
    setBenefitMode('none');
    setSelectedBundleId('');
    setDiscountChoiceTouched(false);
    setDateRange(null);
    setDate('');
    setPaymentChoice('unpaid');
    setServiceOptions({});
    setServiceAddons({});
    setAddonCharges({});
  }, [bookingType]);

  const memberEligible = !accountLoading && membership &&
    membership.billingStatus === 'active' && !membership.cancelledAt;
  const memberPercent = memberEligible ? Math.min(100, Math.max(0, Number(membership.discountPercent) || 0)) : 0;
  const selectedDateCount = bookingType === 'Daycare' && dateRange && dateRange.length === 2
    ? Math.round((Date.UTC(dateRange[1].getFullYear(), dateRange[1].getMonth(), dateRange[1].getDate()) - Date.UTC(dateRange[0].getFullYear(), dateRange[0].getMonth(), dateRange[0].getDate())) / 86400000) + 1
    : date ? 1 : 0;
  const eligibleBundles = !accountLoading && bookingType === 'Daycare' && selectedPets.length === 1
    ? bundles.filter(function (b) {
        return b.status === 'active' && Number(b.remainingDays) >= selectedDateCount && selectedDateCount > 0 &&
          (!b.expiryDate || b.expiryDate >= new Date().toISOString().slice(0, 10)) &&
          (b.familyMemberId == null || String(b.familyMemberId) === String(selectedPets[0].petId));
      }) : [];
  // The bundle is an account-level credit unless explicitly assigned to a pet.
  // Auto-select the first eligible bundle, but respect a staff member's manual choice.
  useEffect(function () {
    if (!discountChoiceTouched && bookingType === 'Daycare' && eligibleBundles.length > 0) {
      setBenefitMode('bundle');
      setSelectedBundleId(String(eligibleBundles[0].id));
    } else if (!discountChoiceTouched) {
      setBenefitMode('none');
      setSelectedBundleId('');
    }
  }, [bookingType, selectedPets, bundles, accountLoading, accountError, discountChoiceTouched]);
  const chosenBundle = eligibleBundles.find(function (b) { return String(b.id) === String(selectedBundleId); });
  const effectivePaymentChoice = benefitMode === 'bundle' && chosenBundle ? 'externally_paid' : paymentChoice;
  const originalAmount = Math.max(0, Number(basePrice) || 0);
  const manualPercent = Math.min(100, Math.max(0, Number(discountValue) || 0));
  const manualAmount = Math.max(0, Number(discountValue) || 0);
  const totalBaseAmount = bookingType === 'Daycare' ? originalAmount * selectedDateCount : originalAmount;
  const discountAmount = benefitMode === 'bundle' && chosenBundle ? totalBaseAmount
    : benefitMode === 'membership' && memberEligible ? totalBaseAmount * memberPercent / 100
    : benefitMode === 'manual' && discountType === 'percent' ? totalBaseAmount * manualPercent / 100
    : benefitMode === 'manual' && discountType === 'amount' ? Math.min(totalBaseAmount, manualAmount) : 0;
  const discountSavings = Number(discountAmount.toFixed(2));
  const finalPrice = Number((totalBaseAmount - discountSavings).toFixed(2));
  const effectiveDiscountPercent = totalBaseAmount ? discountSavings / totalBaseAmount * 100 : 0;
  const promoDescription = benefitMode === 'bundle' && chosenBundle ? 'Daycare bundle (' + selectedDateCount + ' days)'
    : benefitMode === 'membership' && memberEligible ? membership.planType + ' membership (' + memberPercent + '%)'
    : benefitMode === 'manual' && discountType === 'percent' ? manualPercent + '% manual promo'
    : benefitMode === 'manual' && discountType === 'amount' ? '$' + manualAmount.toFixed(2) + ' manual promo' : 'None';

  /* -------------------------------
     AVAILABILITY
     (REMOVED: no longer rendering slots; keep the rest of the component intact)
  -------------------------------- */
  // useEffect(function () {
  //   if (!bookingType || !date) return;
  //
  //   async function loadSlots() {
  //     try {
  //       var res = await fetchServiceAvailability({
  //         serviceType: bookingType,
  //         date: date
  //       });
  //       setAvailableSlots(res.slots || []);
  //     } catch (e) {
  //       setAvailableSlots([]);
  //     }
  //   }
  //
  //   loadSlots();
  // }, [bookingType, date]);

  /* -------------------------------
     SUBMIT
     (kept as-is; only removed slot requirement since slots are no longer rendered)
  -------------------------------- */
  function isValid() {
    return Boolean(selectedOwner && selectedService && bookingType && selectedDateCount > 0 && selectedPets.length > 0 &&
      (!['Grooming', 'Training'].includes(bookingType) || selectedPets.length === 1) &&
      (bookingType !== 'Grooming' || (serviceOptions.selectedSize && serviceOptions.selectedPackage)) &&
      (bookingType !== 'Training' || (serviceOptions.selectedTrainingType && serviceOptions.selectedSessionType)) &&
      (bookingType !== 'Hotel' || (serviceOptions.checkoutDate && serviceOptions.checkoutDate > date)) &&
      basePrice !== '' && Number.isFinite(Number(basePrice)) && Number(basePrice) >= 0 &&
      Number.isFinite(Number(discountValue || 0)) && Number(discountValue || 0) >= 0 &&
      !accountLoading &&
      (benefitMode !== 'membership' || memberEligible) &&
      (benefitMode !== 'bundle' || Boolean(chosenBundle)) &&
      (benefitMode !== 'manual' || Boolean(discountType)) &&
      Object.keys(serviceAddons).every(function (key) { return !serviceAddons[key] || (key === 'walk' && (bookingType === 'Grooming' || bookingType === 'Hotel')) || (addonCharges[key] !== undefined && addonCharges[key] !== '' && Number.isFinite(Number(addonCharges[key])) && Number(addonCharges[key]) >= 0); }));
  }

  function normalizeDate(d) {
    return moment(d).startOf('day').toISOString();
  }

  async function handleProceed() {
    if (submitting) return;
    if (!isValid()) {
      setError('Please complete all required fields.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        userId: selectedOwner.uid,

        serviceId: selectedService.id,

        bookingType,
        date: bookingType === 'Daycare' && dateRange ? moment(dateRange[0]).format('YYYY-MM-DD') : date,
        dates: bookingType === 'Daycare' && dateRange ? Array.from({ length: selectedDateCount }, function (_, i) { return moment(dateRange[0]).add(i, 'days').format('YYYY-MM-DD'); }) : [date],
        selectedPets: selectedPets.map(p => p.petId),
        originalPrice: Number(basePrice || 0),
        discountedPrice: finalPrice,
        benefitMode,
        paymentDisposition: effectivePaymentChoice,
        bundleId: benefitMode === 'bundle' ? chosenBundle.id : null,
        promo: benefitMode === 'manual' ? { type: discountType, value: Number(discountValue || 0) } : null,
        serviceOptions,
        additionalServices: Object.keys(serviceAddons).reduce(function (out, key) {
          out[key] = { selected: Boolean(serviceAddons[key]), charge: Number(addonCharges[key]) || 0 };
          return out;
        }, {}),
        specialNotes: notes
      };

      const res = await createStaffBooking(payload);
      navigate('/console/tasks/reception/reception-bookings');
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message) || 'Failed to create booking. Please check the server logs.');
    } finally {
      setSubmitting(false);
    }
  }

  /* -------------------------------
     RENDER
  -------------------------------- */
  return (
    <div className="rbc-wrap">
      <div className="rbc-topbar">
        <BackButton />
      </div>

      {!selectedOwner ? (
        <>
          <h2 className="rbc-title">Find Owner or Pet</h2>

          <input
            className="rbc-input"
            placeholder="Search name or email…"
            value={searchTerm}
            onChange={function (e) {
              setSearchTerm(e.target.value);
            }}
          />

          {loadingClients ? (
            <p className="rbc-loading">Loading clients…</p>
          ) : results.length === 0 ? (
            <p className="rbc-empty">No owners or pets match your search.</p>
          ) : (
            <>
              <p className="rbc-results-count">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, results.length)} of {results.length} results
              </p>
              <div className="rbc-list">
                {visibleResults.map(function (row) {
                  const isPet = row.kind === 'pet';
                  return (
                    <button
                      type="button"
                      key={isPet ? 'pet-' + row.petId : 'owner-' + row.uid}
                      className="rbc-row rbc-client-row"
                      onClick={function () { handleSelect(row); }}
                    >
                      <div className="rbc-client-avatar">
                        {row.image ? (
                          <img src={row.image} alt="" />
                        ) : (
                          <span>{(row.name || '?').charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="rbc-client-info">
                        <div className="rbc-client-name-line">
                          <strong>{row.name || 'Unnamed ' + (isPet ? 'pet' : 'owner')}</strong>
                          {!isPet && <span className="rbc-client-type">Owner</span>}
                        </div>
                        <div className="rbc-client-meta">
                          {isPet
                            ? (row.type || 'Pet') + (row.breed ? ' · ' + row.breed : '') + ' · Owner: ' + (row.ownerName || 'Unknown')
                            : row.email}
                        </div>
                        {!isPet && (
                          <div className="rbc-pack" aria-label={row.name + "'s pets"}>
                            {row.pets.length ? row.pets.map(function (pet) {
                              return (
                                <div className="rbc-pack-member" key={pet.petId}>
                                  <div className="rbc-pack-photo">
                                    {pet.image ? (
                                      <img src={pet.image} alt={pet.petName} />
                                    ) : (
                                      <span>{(pet.petName || '?').charAt(0).toUpperCase()}</span>
                                    )}
                                  </div>
                                  <span className="rbc-pack-name">{pet.petName}</span>
                                  <span className="rbc-pack-kind">{pet.type || 'Pet'}</span>
                                </div>
                              );
                            }) : <span className="rbc-client-pets">No pets yet</span>}
                          </div>
                        )}
                      </div>
                      {isPet && <span className="rbc-client-type">{row.type || 'Pet'}</span>}
                    </button>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <nav className="rbc-pagination" aria-label="Client results pages">
                  <button className="rbc-btn" type="button" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
                  <span className="rbc-pageinfo">Page {currentPage} of {totalPages}</span>
                  <button className="rbc-btn" type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
                </nav>
              )}
            </>
          )}
          {error && <p className="rbc-error">{error}</p>}
        </>
      ) : (
        <>
          <div className="rbc-booking-header">
            <div>
              <h2 className="rbc-title">Create Booking</h2>
              <p className="rbc-booking-subtitle">For {selectedOwner.name}</p>
            </div>
            <button type="button" className="rbc-btn rbc-btn--ghost" onClick={function () {
              setSelectedOwner(null);
              setSelectedPets([]);
              setDiscountChoiceTouched(false);
              setBenefitMode('none');
              setSelectedBundleId('');
              setError(null);
            }}>Change client</button>
          </div>

          <div className="rbc-filter">
            <label>Select pets <span className="rbc-required">*</span></label>
            <p className="rbc-field-hint">Select one or more pets for this booking.</p>
            <div className="rbc-select-pets">
              {ownerPets.map(function (p) {
                const active = selectedPets.some(function (selected) { return selected.petId === p.petId; });
                return (
                  <button
                    key={p.petId}
                    type="button"
                    aria-pressed={active}
                    className={'rbc-select-pet' + (active ? ' rbc-select-pet--active' : '')}
                    onClick={function () {
                      setSelectedPets(function (previous) {
                        setDiscountChoiceTouched(false);
                        return active
                          ? previous.filter(function (selected) { return selected.petId !== p.petId; })
                          : previous.concat([p]);
                      });
                    }}
                  >
                    <span className="rbc-select-pet-photo">
                      {p.image ? <img src={p.image} alt="" /> : <span>{(p.petName || '?').charAt(0).toUpperCase()}</span>}
                    </span>
                    <strong>{p.petName}</strong>
                    <small>{p.type || 'Pet'}{p.breed ? ' · ' + p.breed : ''}</small>
                    <span className="rbc-pet-selected">{active ? '✓ Selected' : 'Select'}</span>
                  </button>
                );
              })}
            </div>
            {ownerPets.length === 0 && <p className="rbc-empty">No pets on this client account. Add a pet to the client's profile first.</p>}
            <p className="rbc-field-hint">{selectedPets.length} of {ownerPets.length} pets selected</p>
          </div>

          <div className="rbc-account-status">
            <div className="rbc-account-panel">
              <h3>Membership</h3>
              {accountLoading ? <p>Loading…</p> : accountError ? <p>Status unavailable</p> : membership ? (
                <>
                  <strong>{membership.planType} Membership</strong>
                  <p>Status: {membership.billingStatus}{membership.cancelledAt ? " (cancelled)" : ""}</p>
                  <p>Member discount: {membership.discountPercent}%</p>
                  {membership.nextBillingDate && <p>Next billing: {membership.nextBillingDate}</p>}
                </>
              ) : <p>No club membership found</p>}
            </div>
            <div className="rbc-account-panel">
              <h3>Daycare bundles</h3>
              {accountLoading ? <p>Loading…</p> : accountError ? <p>Status unavailable</p> : bundles.length ? bundles.map(function (bundle) {
                var pet = ownerPets.find(function (p) { return String(p.petId) === String(bundle.familyMemberId); });
                return <div className="rbc-bundle-row" key={bundle.id}>
                  <strong>{bundle.bundleType}</strong>
                  <span>{bundle.remainingDays} / {bundle.totalDays} days remaining</span>
                {bookingType === 'Daycare' && selectedDateCount > Number(bundle.remainingDays) && <small>Selected {selectedDateCount} dates exceed this bundle by {selectedDateCount - Number(bundle.remainingDays)} day(s). Choose fewer dates or another payment method.</small>}
                {bookingType === 'Daycare' && selectedDateCount > 0 && <span className="rbc-bundle-preview">After booking: {benefitMode === 'bundle' && chosenBundle && String(chosenBundle.id) === String(bundle.id) ? bundle.remainingDays - selectedDateCount : bundle.remainingDays} days ({benefitMode === 'bundle' && chosenBundle && String(chosenBundle.id) === String(bundle.id) ? selectedDateCount + ' being used' : 'not applied'})</span>}
                  <small>{pet ? pet.petName : "Account bundle"} · {bundle.status}</small>
                  {bundle.expiryDate && <small>Expires: {bundle.expiryDate}</small>}
                </div>;
              }) : <p>No active daycare bundles</p>}
            </div>
          </div>
          <p className="rbc-field-hint">Select one discount source below. Membership and bundle offers cannot be stacked.</p>

          <div className="rbc-filter">
            <label>Service <span className="rbc-required">*</span></label>
            <select
              className="rbc-select"
              value={bookingType}
              onChange={function (e) {
                setBookingType(e.target.value);
              }}
            >
              <option value="">Select</option>
              <option value="Daycare">Daycare</option>
              <option value="Grooming">Grooming</option>
              <option value="Training">Training</option>
              <option value="Adventure">Adventure</option>
              <option value="Hotel">Hotel</option>
              <option value="Shuttle">Shuttle</option>
            </select>
          </div>


          {['Grooming', 'Training'].includes(bookingType) && selectedPets.length > 1 && <p className="rbc-error">Select one pet for accurate service pricing.</p>}
          {serviceError && <p className="rbc-error">Service records could not be loaded. Booking submission is disabled.</p>}
          {bookingType && !selectedService && !serviceError && <p className="rbc-field-hint">Service record unavailable. Please verify this service exists before creating a booking.</p>}
          {['Grooming', 'Training', 'Shuttle', 'Hotel'].includes(bookingType) && (
            <div className="rbc-filter rbc-service-options">
              <h3>{bookingType} options</h3>
              {bookingType === 'Grooming' && (
                <>
                  <label>Pet size</label>
                  <select className="rbc-select" value={serviceOptions.selectedSize || ''} onChange={function(e) { setOption('selectedSize', e.target.value); }}>
                    <option value="">Select size</option>
                    {['small','medium','large','x-large'].map(function(v) { return <option key={v} value={v}>{v}</option>; })}
                  </select>
                  <label>Grooming package</label>
                  <select className="rbc-select" value={serviceOptions.selectedPackage || ''} onChange={function(e) { setOption('selectedPackage', e.target.value); }}>
                    <option value="">Select package</option>
                    {['nail','bath','touchup','grooming'].concat(selectedPets.some(function(p) { return p.type === 'Cat'; }) ? [] : ['teeth']).map(function(v) { return <option key={v} value={v}>{v === 'grooming' ? 'Full groom' : v === 'touchup' ? 'Touch-up' : v}</option>; })}
                  </select>
                  <label>Grooming add-ons</label>
                  <div className="rbc-addon-options">
                    {groomingAddons.map(function (name) {
                      return <label key={name}><input type="checkbox" checked={Boolean(groomingAddonFlags[name])} onChange={function () {
                        setOption('groomingAddons', Object.assign({}, groomingAddonFlags, { [name]: !groomingAddonFlags[name] }));
                      }} /> {name} (+${Number(groomingAddonCatalog[name]).toFixed(2)})</label>;
                    })}
                    <label><input type="checkbox" checked={Boolean(serviceOptions.mattedHair)} onChange={function () { setOption('mattedHair', !serviceOptions.mattedHair); }} /> Matted coat (+$20.00)</label>
                    <label><input type="checkbox" checked={Boolean(serviceOptions.skunkTreatment)} onChange={function () { setOption('skunkTreatment', !serviceOptions.skunkTreatment); }} /> Skunk treatment (+$50.00)</label>
                  </div>
                  <label>Styling, coat and handling instructions</label>
                  <textarea className="rbc-input" rows={3} value={serviceOptions.groomingInstructions || ''} onChange={function(e) { setOption('groomingInstructions', e.target.value); }} />
                </>
              )}
              {bookingType === 'Training' && (
                <>
                  <label>Training program</label>
                  <select className="rbc-select" value={serviceOptions.selectedTrainingType || ''} onChange={function(e) { setOption('selectedTrainingType', e.target.value); }}>
                    <option value="">Select program</option>
                    <option value="puppy">Puppy</option><option value="basic">Basic obedience</option>
                    <option value="behavior">Behavior modification</option><option value="akc">AKC</option>
                  </select>
                  <label>Session format</label>
                  <select className="rbc-select" value={serviceOptions.selectedSessionType || ''} onChange={function(e) { setOption('selectedSessionType', e.target.value); }}>
                    <option value="">Select format</option><option value="group">Group</option><option value="private">Private</option>
                  </select>
                  <label>Training goals / behavior notes</label>
                  <textarea className="rbc-input" rows={3} value={serviceOptions.trainingGoals || ''} onChange={function(e) { setOption('trainingGoals', e.target.value); }} />
                </>
              )}
              {bookingType === 'Shuttle' && (
                <>
                  <label>Shuttle service</label>
                  <select className="rbc-select" value={serviceOptions.shuttleType || ''} onChange={function(e) { setOption('shuttleType', e.target.value); }}>
                    <option value="">Select ride purpose</option>
                    {['Airport','Daycare','Hotel','Training','Grooming','Adventure'].map(function(v) { return <option key={v} value={v}>{v}</option>; })}
                  </select>
                  <label>Estimated driving distance (miles; $3/mile plus base fare)</label>
                  <input type="number" min="0" step="0.1" className="rbc-input" value={serviceOptions.distanceMiles || ''} onChange={function(e) { setOption('distanceMiles', e.target.value); }} />
                  <label>Pickup location</label>
                  <input className="rbc-input" value={serviceOptions.pickUpLocation || ''} onChange={function(e) { setOption('pickUpLocation', e.target.value); }} />
                  <label>Drop-off location</label>
                  <input className="rbc-input" value={serviceOptions.dropOffLocation || ''} onChange={function(e) { setOption('dropOffLocation', e.target.value); }} />
                  <label>Pickup time</label>
                  <input type="time" className="rbc-input" value={serviceOptions.pickupTime || ''} onChange={function(e) { setOption('pickupTime', e.target.value); }} />
                  <label>Emergency contact name and phone</label>
                  <input className="rbc-input" placeholder="Name" value={serviceOptions.emergencyContactName || ''} onChange={function(e) { setOption('emergencyContactName', e.target.value); }} />
                  <input className="rbc-input" placeholder="Phone" value={serviceOptions.emergencyContactNumber || ''} onChange={function(e) { setOption('emergencyContactNumber', e.target.value); }} />
                </>
              )}
              {bookingType === 'Hotel' && (
                <>
                  <label>Check-out date</label>
                  <input type="date" className="rbc-input" min={date} value={serviceOptions.checkoutDate || ''} onChange={function(e) { setOption('checkoutDate', e.target.value); }} />
                  <label>Drop-off / pick-up times</label>
                  <input type="time" className="rbc-input" value={serviceOptions.dropoffTime || ''} onChange={function(e) { setOption('dropoffTime', e.target.value); }} />
                  <input type="time" className="rbc-input" value={serviceOptions.pickupTime || ''} onChange={function(e) { setOption('pickupTime', e.target.value); }} />
                  <label>Meal plan</label>
                  <select className="rbc-select" value={serviceOptions.foodPreference || ''} onChange={function(e) { setOption('foodPreference', e.target.value); }}>
                    <option value="">Select plan</option><option value="own">Bring own food</option><option value="basic">Basic</option><option value="premium">Premium</option>
                  </select>
                </>
              )}
              <label>Add-on services</label>
              <div className="rbc-addon-options">
                {(bookingType === 'Hotel' ? ['grooming','training','shuttle','adventure','walk'] :
                  bookingType === 'Grooming' ? ['shuttle','walk'] :
                  bookingType === 'Training' ? ['grooming','shuttle','walk'] : []).map(function(name) {
                  const pricedByCalculator = name === 'walk' && (bookingType === 'Grooming' || bookingType === 'Hotel');
                  return <div key={name} className="rbc-addon-entry">
                    <label><input type="checkbox" checked={Boolean(serviceAddons[name])} onChange={function() { toggleAddon(name); }} /> {name.charAt(0).toUpperCase() + name.slice(1)}</label>
                    {serviceAddons[name] && !pricedByCalculator && <label>Charge ($) <input type="number" className="rbc-input" min="0" step="0.01" value={addonCharges[name] === undefined ? '' : addonCharges[name]} placeholder="Enter charge" onChange={function(e) {
                      setAddonCharges(function(prev) { return Object.assign({}, prev, { [name]: e.target.value }); });
                    }} /></label>}
                    {serviceAddons[name] && pricedByCalculator && <small>Calculated with client booking pricing</small>}
                  </div>;
                })}
              </div>
              {pricedAddons.length > 0 && <div className="rbc-price-breakdown"><strong>Included add-on charges</strong>{pricedAddons.map(function (row, i) { return <div key={i}><span>{row.name}</span><strong>From ${row.amount.toFixed(2)}</strong></div>; })}</div>}
              <p className="rbc-field-hint">Prices use the client-side service calculators and available catalog data. Review totals before saving; unavailable price rules and availability checks are not included.</p>
            </div>
          )}
          <div className="rbc-filter">
            <label>Date <span className="rbc-required">*</span></label>
            {bookingType === 'Daycare' ? (
              <>
                <Calendar selectRange calendarType="gregory"
                  minDate={new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())}
                  value={dateRange}
                  onChange={function (range) {
                    if (!Array.isArray(range) || range.length !== 2) return;
                    setDateRange(range);
                    setDate(moment(range[0]).format('YYYY-MM-DD'));
                    setSelectedBundleId('');
                    setDiscountChoiceTouched(false);
                  }}
                />
                <p className="rbc-field-hint">{dateRange ? moment(dateRange[0]).format('MMM D, YYYY') + ' – ' + moment(dateRange[1]).format('MMM D, YYYY') + ' · ' + selectedDateCount + ' day(s)' : 'Select a start and end date on the calendar.'}</p>
              </>
            ) : <input type="date" className="rbc-input" value={date} onChange={function (e) { setDate(e.target.value); }} />}
          </div>

          {/* Time Slot UI REMOVED as requested */}

          {/* Price: now editable (manual set price) */}
          <div className="rbc-filter">
            <label>Price including selected add-ons ($) <span className="rbc-required">*</span></label>
            <div className="rbc-price-breakdown" aria-live="polite">
              <div><span>Calculated service + add-ons</span><strong>${totalBaseAmount.toFixed(2)}</strong></div>
              <div><span>Discount applied</span><strong>{promoDescription}</strong></div>
              <div><span>Discount amount</span><strong>-${discountSavings.toFixed(2)} ({effectiveDiscountPercent.toFixed(1)}%)</strong></div>
              <div><span>Final amount</span><strong>${finalPrice.toFixed(2)}</strong></div>
              {membership && membership.billingStatus === 'active' && !membership.cancelledAt && (
                <p className="rbc-field-hint">Membership available: {membership.discountPercent}%. Select it under Discount source to apply.</p>
              )}
            </div>
            <input
              type="number"
              min="0"
              step="0.01"
              className="rbc-input"
              value={basePrice}
              onChange={function (e) {
                setBasePrice(e.target.value);
              }}
            />
            <div className='rbc-number-total' style={{ marginTop: 8 }}>
              <strong>Total: ${finalPrice.toFixed(2)}</strong>
            </div>
          </div>

          <div className="rbc-filter">
            <label>Discount source</label>
            <select className="rbc-select" value={benefitMode} onChange={function (e) {
              setDiscountChoiceTouched(true);
              setBenefitMode(e.target.value);
              setSelectedBundleId(e.target.value === 'bundle' && eligibleBundles.length ? String(eligibleBundles[0].id) : '');
              setPaymentChoice('unpaid');
            }}>
              <option value="none">No discount</option>
              {memberEligible && <option value="membership">{membership.planType} membership — {memberPercent}%</option>}
              {eligibleBundles.length > 0 && <option value="bundle">Redeem daycare bundle — 100%</option>}
              <option value="manual">Manual promotion</option>
            </select>
            {benefitMode === 'bundle' && (
              <select className="rbc-select" value={selectedBundleId} onChange={function (e) { setSelectedBundleId(e.target.value); }}>
                <option value="">Choose bundle to redeem</option>
                {eligibleBundles.map(function (b) {
                  return <option key={b.id} value={b.id}>{b.bundleType} — {b.remainingDays} days left</option>;
                })}
              </select>
            )}
            {benefitMode === 'manual' && (
              <>
                <select className="rbc-select" value={discountType} onChange={function (e) {
                  setDiscountType(e.target.value); setDiscountValue('');
                }}>
                  <option value="">Select manual discount</option>
                  <option value="percent">% Discount</option>
                  <option value="amount">$ Discount</option>
                </select>
                {discountType && <input className="rbc-input" type="number" min="0" step="0.01" value={discountValue}
                  onChange={function (e) { setDiscountValue(e.target.value); }} />}
              </>
            )}
            {benefitMode === 'bundle' && <p className="rbc-field-hint">{selectedDateCount} day(s) will be deducted from the chosen bundle when pending bookings are created. Bundle redemption supports one pet per booking.</p>}
          </div>

          <div className="rbc-filter">
            <label>Payment handling</label>
            {benefitMode === 'bundle' && chosenBundle ? (
              <div className="rbc-payment-notice">
                <strong>Covered by prepaid daycare bundle</strong>
                <span>No new payment is charged. {selectedDateCount} bundle day(s) will be redeemed when bookings are created.</span>
                <button type="button" className="rbc-btn" onClick={function () {
                  setDiscountChoiceTouched(true);
                  setBenefitMode('none');
                  setSelectedBundleId('');
                  setPaymentChoice('unpaid');
                }}>Create unpaid instead (do not use bundle)</button>
              </div>
            ) : (
              <select className="rbc-select" value={paymentChoice} onChange={function (e) { setPaymentChoice(e.target.value); }}>
                <option value="unpaid">Unpaid — collect payment later</option>
                <option value="externally_paid">Already paid outside Pawsible — record only</option>
              </select>
            )}
            <p className="rbc-field-hint">This form does not charge a card. Mark as already paid only after confirming receipt of payment.</p>
          </div>

          <div className="rbc-filter">
            <label>Special notes</label>
            <textarea
              className="rbc-input"
              rows={3}
              placeholder="Handling instructions, pickup arrangements, staff notes…"
              value={notes}
              onChange={function (e) {
                setNotes(e.target.value);
              }}
            />
          </div>

          <div className="rbc-booking-summary">
            <strong>Booking summary</strong>
            <span>Client: {selectedOwner.name}</span>
            <span>Pets: {selectedPets.length ? selectedPets.map(p => p.petName).join(', ') : 'None selected'}</span>
            <span>Service: {bookingType || 'Not selected'}</span>
            <span>Dates: {bookingType === 'Daycare' && dateRange ? moment(dateRange[0]).format('MMM D') + ' – ' + moment(dateRange[1]).format('MMM D') : (date || 'Not selected')} ({selectedDateCount} day(s))</span>
            <span>Discount: {promoDescription} — -${discountSavings.toFixed(2)} ({effectiveDiscountPercent.toFixed(1)}%)</span>
            <span>Total: ${finalPrice.toFixed(2)}</span>
            <span>Payment: {benefitMode === 'bundle' && chosenBundle ? 'Covered by prepaid bundle' : paymentChoice === 'unpaid' ? 'Unpaid — collect later' : 'Recorded as already paid externally'}</span>
            <span className="rbc-field-hint">Creates scheduled booking tickets. No card charge is initiated here; unpaid tickets can be collected later.</span>
          </div>

          {error && <p className="rbc-error" role="alert">{error}</p>}

          <button
            className="rbc-btn rbc-btn--primary"
            disabled={!isValid() || submitting}
            onClick={handleProceed}
          >
            {submitting ? 'Creating…' : 'Create pending booking'}
          </button>
        </>
      )}
    </div>
  );
};

export default ReceptionCreateBooking;
